import type { AtomKind, AtomRecord, AtomStatus } from "../model/atom.js";
import type { CellEffect, CellRecord } from "../model/cell.js";
import type { JsonObject } from "../model/common.js";
import type { BoardState } from "../model/position.js";
import { CliError } from "../errors/index.js";
import { isLegalCell } from "./legality.js";

type MutationHelpers = {
  allocateAtomId(requestedId: string | undefined): string;
  allocateCellId(requestedId: string | undefined): string;
};

type ProofVerdict = "fail" | "pass";

export function upsertAtom(
  state: BoardState,
  helpers: MutationHelpers,
  input: {
    atomId: string | undefined;
    body: string;
    kind: AtomKind;
    status: AtomStatus | undefined;
    tags: string[];
    title: string | undefined;
  },
) {
  const now = new Date().toISOString();
  const atomId = input.atomId ?? helpers.allocateAtomId(undefined);
  const existing = state.atoms[atomId];
  const nextStatus = input.status ?? existing?.status ?? "active";
  const nextTitle = input.title ?? deriveTitle(input.body, input.kind);
  if (
    existing !== undefined &&
    existing.body === input.body &&
    existing.kind === input.kind &&
    existing.status === nextStatus &&
    existing.title === nextTitle &&
    sameStringList(existing.tags, input.tags)
  ) {
    return {
      atom: existing,
      movePayload: {
        atomId,
        kind: existing.kind,
      } satisfies JsonObject,
      moveSummary: `Keep atom ${atomId}`,
    };
  }

  const atom: AtomRecord = {
    body: input.body,
    createdAt: existing?.createdAt ?? now,
    id: atomId,
    kind: input.kind,
    status: nextStatus,
    tags: input.tags,
    title: nextTitle,
    updatedAt: now,
  };

  state.atoms[atomId] = atom;
  return {
    atom,
    movePayload: {
      atomId,
      kind: atom.kind,
    } satisfies JsonObject,
    moveSummary: `${existing === undefined ? "Create" : "Update"} atom ${atomId}`,
  };
}

export function upsertCell(
  state: BoardState,
  helpers: MutationHelpers,
  input: {
    cellId: string | undefined;
    effect: CellEffect;
    from: string;
    to: string;
  },
) {
  const fromAtom = requireAtom(state, input.from);
  const toAtom = requireAtom(state, input.to);
  if (!isLegalCell(fromAtom.kind, input.effect, toAtom.kind)) {
    throw new CliError(
      "ILLEGAL_CELL",
      `Cannot ${input.effect} from ${fromAtom.kind} "${fromAtom.id}" to ${toAtom.kind} "${toAtom.id}"`,
    );
  }

  const now = new Date().toISOString();
  const semanticExisting = input.cellId === undefined
    ? Object.values(state.cells).find((cell) =>
      cell.status === "active" &&
      cell.effect === input.effect &&
      cell.from === input.from &&
      cell.to === input.to
    )
    : undefined;
  const cellId = input.cellId ?? semanticExisting?.id ?? helpers.allocateCellId(undefined);
  const existing = state.cells[cellId];
  if (
    existing !== undefined &&
    existing.status === "active" &&
    existing.effect === input.effect &&
    existing.from === input.from &&
    existing.to === input.to
  ) {
    return {
      cell: existing,
      movePayload: {
        cellId,
        effect: existing.effect,
        from: existing.from,
        to: existing.to,
      } satisfies JsonObject,
      moveSummary: `Keep cell ${cellId}`,
    };
  }

  const cell: CellRecord = {
    createdAt: existing?.createdAt ?? now,
    effect: input.effect,
    from: input.from,
    id: cellId,
    status: "active",
    to: input.to,
    updatedAt: now,
  };
  state.cells[cellId] = cell;

  return {
    cell,
    movePayload: {
      cellId,
      effect: cell.effect,
      from: cell.from,
      to: cell.to,
    } satisfies JsonObject,
    moveSummary: `${existing === undefined ? "Create" : "Update"} cell ${cellId}`,
  };
}

export function selectLine(
  state: BoardState,
  input: {
    pathId: string;
  },
) {
  const pathAtom = requireAtom(state, input.pathId);
  if (pathAtom.kind !== "path") {
    throw new CliError("INVALID_LINE", `Atom "${input.pathId}" is not a path`);
  }

  const previous = state.line.selectedPathId;
  if (previous === input.pathId && pathAtom.status === "selected") {
    return {
      movePayload: {
        pathId: input.pathId,
      } satisfies JsonObject,
      moveSummary: `Keep line ${input.pathId}`,
      path: pathAtom,
    };
  }

  if (previous !== undefined && previous !== input.pathId) {
    const previousAtom = state.atoms[previous];
    if (previousAtom !== undefined && previousAtom.status === "selected") {
      previousAtom.status = "active";
      previousAtom.updatedAt = new Date().toISOString();
    }
  }

  pathAtom.status = "selected";
  pathAtom.updatedAt = new Date().toISOString();
  state.line.selectedPathId = input.pathId;

  return {
    movePayload: {
      pathId: input.pathId,
    } satisfies JsonObject,
    moveSummary: `Select line ${input.pathId}`,
    path: pathAtom,
  };
}

export function recordProof(
  state: BoardState,
  helpers: MutationHelpers,
  input: {
    body: string;
    proofId: string | undefined;
    targetAtomId: string;
    verdict: ProofVerdict;
  },
) {
  const targetAtom = requireAtom(state, input.targetAtomId);
  const now = new Date().toISOString();
  const proofId = input.proofId ?? helpers.allocateAtomId(undefined);
  const existingProof = state.atoms[proofId];
  const proof: AtomRecord = {
    body: input.body,
    createdAt: existingProof?.createdAt ?? now,
    id: proofId,
    kind: "proof",
    status: input.verdict === "pass" ? "resolved" : "active",
    tags: existingProof?.tags ?? [],
    title: deriveTitle(input.body, "proof"),
    updatedAt: now,
  };
  state.atoms[proofId] = proof;

  const proofCellId = helpers.allocateCellId(undefined);
  state.cells[proofCellId] = {
    createdAt: now,
    effect: "proves",
    from: proofId,
    id: proofCellId,
    status: "active",
    to: targetAtom.id,
    updatedAt: now,
  };

  if (input.verdict === "pass") {
    neutralizeFindingsForTarget(state, helpers, proofId, targetAtom.id);
  } else {
    const findingId = helpers.allocateAtomId(undefined);
    state.atoms[findingId] = {
      body: input.body,
      createdAt: now,
      id: findingId,
      kind: "finding",
      status: "active",
      tags: [],
      title: `Finding against ${targetAtom.id}`,
      updatedAt: now,
    };
    const threatCellId = helpers.allocateCellId(undefined);
    state.cells[threatCellId] = {
      createdAt: now,
      effect: "threatens",
      from: findingId,
      id: threatCellId,
      status: "active",
      to: targetAtom.id,
      updatedAt: now,
    };
  }

  return {
    movePayload: {
      proofId,
      targetAtomId: targetAtom.id,
      verdict: input.verdict,
    } satisfies JsonObject,
    moveSummary: `Record ${input.verdict} proof for ${targetAtom.id}`,
    proof,
  };
}

function neutralizeFindingsForTarget(
  state: BoardState,
  helpers: MutationHelpers,
  proofId: string,
  targetAtomId: string,
) {
  const now = new Date().toISOString();
  for (const cell of Object.values(state.cells)) {
    if (
      cell.status !== "active" ||
      cell.effect !== "threatens" ||
      cell.to !== targetAtomId
    ) {
      continue;
    }

    const finding = state.atoms[cell.from];
    if (finding?.kind !== "finding") {
      continue;
    }

    finding.status = "resolved";
    finding.updatedAt = now;
    cell.status = "inactive";
    cell.updatedAt = now;

    const mergeCellId = helpers.allocateCellId(undefined);
    state.cells[mergeCellId] = {
      createdAt: now,
      effect: "merges",
      from: proofId,
      id: mergeCellId,
      status: "active",
      to: finding.id,
      updatedAt: now,
    };
  }
}

function sameStringList(left: readonly string[], right: readonly string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function requireAtom(state: BoardState, atomId: string) {
  const atom = state.atoms[atomId];
  if (atom === undefined) {
    throw new CliError("ATOM_NOT_FOUND", `Atom "${atomId}" was not found`);
  }
  return atom;
}

function deriveTitle(body: string, kind: AtomKind | "proof") {
  const compact = body.trim().split(/\r?\n/, 1)[0]?.trim() ?? "";
  if (compact.length > 0) {
    return compact.slice(0, 80);
  }
  return `${kind} ${new Date().toISOString()}`;
}
